import { FetchAPI, FieldAPI, FormAPI, PanelAPI, PhoneNumberAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnBeforeFetch, OnBeforeSubmit, OnChange, OnEdit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, EditEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BrokerService, HttpService, LanguageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { ToastResponse } from '../../interfaces/ToastResponse';
import { showToastNotificationMessage } from '../../utils/toast-notification-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { Country } from '@philips-internal/oswald-ui-constants';
import UserContext from '../../utils/user-context';
import { LocaleTypes } from '../../constants/locale-types';
import ProfileModalUploadService from '../../services/profile-modal-upload-service';
import { BS_REFRESH_FORM_COMPL_PESSOA_FISICA } from '../../constants/application-constants';
import { InformationAliases } from '../../codegen';
import ProfileBaseController from '../profile-base-controller';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';

interface Target {
  state: State;
}

interface FieldTypes {
  dsValorNew: string | Value;
  dsValorOld: string | Value;
  nmAtributo: string;
  nrSeqDocumento?: string | Value;
}

interface State {
  bodyToUpdate: Record<string, Value>;
  oldValues: Record<string, FieldsValue>;
  record: Record<string, FieldsValue>;
}

interface FieldsValue {
  boliviaCity: BoliviaCity;
  boliviaDepartments: StateBoliviaEventProps;
  boliviaStateProvince: StateProvinceBoliviaEventProps;
  description: string;
  dsEstado: string;
  dsMunicipio: string;
  dsStateProvince: string;
  nrSeqPais: string;
  sequence: string;
  sgEstado: string;
}

interface FieldsRequireProof {
  dsDocumento: string;
  ieAttributo: string;
  nrSequenciaDoc: number;
}

interface StateBoliviaEventProps {
  description: string,
  information: string;
  sequence: string
}

interface StateProvinceBoliviaEventProps {
  cdStateProvince: number,
  dsStateProvince: string,
  ieSituation: string
}

interface BodyProps {
  campos: FieldTypes[];
  insertVal:Record<string, Value>[];
}

interface AddressCatalogProps {
  dsCatalogo: string;
  nrSequencia: number;
}

interface AddressFieldsProps {
  ieFisicaJuridica: string;
  ieInformacao: string;
  ieObrigatorio: string;
  irReadOnly: string;
}

interface BodyProps {
  campos: FieldTypes[];
  insertVal: Record<string, Value>[];
}

interface BoliviaCity {
  cdMunicipio: string;
  dsMunicipio: string;
  information: string;
}

enum DemographicInformationFieldsType{
  STATE = 'estado',
  STATE_ACRONYM = 'sgEstado',
  STATE_NAME = 'dsEstado'
}

enum ConfigurationEnderecoField {
  COMPLEMENTO = 'COMPLEMENTO',
  NUMERO = 'NUMERO',
  NUMERO_INT = 'NUMERO_INT',
  PAIS = 'PAIS',
  RUA_VIALIDADE = 'RUA_VIALIDADE',
  TIPO_BAIRRO = 'TIPO_BAIRRO',
  ESTADO_PROVINCI = 'ESTADO_PROVINCI',
  CODIGO_POSTAL = 'CODIGO_POSTAL',
  MUNICIPIO = 'MUNICIPIO',
  DISTRITO = 'DISTRITO',
  BAIRRO_VILA = 'BAIRRO_VILA',
  NUM_EXT_ALFA = 'NUM_EXT_ALFA',
  LOCALIDADE_AREA = 'LOCALIDADE_AREA',
  TIPO_LOGRAD = 'TIPO_LOGRAD'
}

enum TipoFormularioEnum {
  INFORMACOES_DEMOGRAFICAS = 'INFORMACOES_DEMOGRAFICAS'
}

@Controller({ component: 'layout-demographic-info', fragment: 'information' })
class DemographicInformationController extends ProfileBaseController {

  state: State = {
    bodyToUpdate: {},
    oldValues: {},
    record: {},
  };

  @Inject('form-demographic')
    formDemo!: FormAPI;

  @Inject('panel-loader')
    panelLoader!: PanelAPI;

  @Inject('fetch-demograpgic')
    fetchFormDemographicAPI!: FetchAPI;

  @Inject('field-post-code')
    fieldPostCodeAPI!: FieldAPI;

  @Inject('field-state')
    fieldStateAPI!: FieldAPI;

  @Inject('field-state-bolivia')
    fieldStateBoliviaAPI!: FieldAPI;

  @Inject('field-state-province-bolivia')
    fieldStateProvinceBoliviaAPI!: FieldAPI;

  @Inject('field-city-display')
    fieldCityDisplayAPI!: FieldAPI;

  @Inject('field-city-bolivia')
    fieldCityBoliviaAPI!: FieldAPI;

  @Inject('fetch-state-province-bolivia')
    fetchStateProvinceBoliviaAPI!: FetchAPI;

  @Inject('fetch-city-bolivia')
    fetchCityBoliviaAPI!: FetchAPI;

  @Inject('field-address ')
    fieldAddressAPI!: FieldAPI;

  @Inject('field-complement-bolivia')
    fieldComplementBoliviaAPI!: FieldAPI

  @Inject('field-nrTelefone')
    fieldNrTelefoneAPI!: FieldAPI;

  @Inject('field-number')
    fieldNumberAPI!: FieldAPI;

  @Inject('field-neighborhood')
    fieldNeighborhoodAPI!: FieldAPI;

  @Inject('field-localidade-bolivia')
    fieldLocalidadeBoliviaAPI!: FieldAPI;

  @Inject('field-street-bolivia')
    fieldStreetBolivia!: FieldAPI;

  @Inject('field-neighborhood-bolivia')
    fieldNeighborhoodBolivia!: FieldAPI;

  @Inject(InformationAliases.FIELD_ADDRESS_CATALOG)
    fieldAddressCatalogAPI!: FieldAPI;

  @Inject(InformationAliases.FETCH_STATE_BOLIVIA)
    fetchStateBoliviaApi!: FetchAPI;

  private language = LanguageService.getCurrentLanguage();

  @OnEdit({ target: ['form-demographic'] })
  onEdit(event: EditEvent): void {
    const target = event.target as unknown as Target;
    this.state.oldValues = target.state.record || { undefined };
  }

  @OnMount({target: 'phone-number-nrTelefone'})
  handleLoadNrTelefone(event: MountEvent): void {
    const phoneNumber = event.target as PhoneNumberAPI;
    const phoneCountry = LanguageService.getCurrentLanguage().split('-')[1].toLocaleUpperCase();
    phoneNumber.setCountry(phoneCountry as Country);
  }

  @OnBeforeSubmit({ target: ['form-demographic'] })
  onBeforeSubmitEvent(event: SubmitEvent): void {
    let newValues = event.record as Record<string, FieldsValue>;
    const oldValues = this.state.oldValues;

    newValues = {
      ...event.record as Record<string, FieldsValue>,
      sgEstado: event.record?.estado?.sgEstado
    }

    const campos: FieldTypes[] = [];
    const insertVal: Record<string, Value>[] = [];
    const body = { campos, insertVal };
    const updatedFields: string[] = [];

    if(this.language === LocaleTypes.BOLIVIA) {
      newValues = this.addBoliviaFieldsToNewValues(newValues, updatedFields, body, oldValues);
    } else {
      newValues = this.addFieldsToNewValues(newValues, updatedFields, body, oldValues);
    }

    if(!newValues?.nrSequencia) {
      if(Object.keys(newValues).length == 0) {
        return;
      }
      newValues.ieTipoComplemento = {vlDominio: 1} as unknown as FieldsValue;
      body.insertVal.push(newValues);
    }

    this.state.bodyToUpdate = body;
    this.getFieldsRequired(updatedFields, body);
  }

  createStateField(attributeName: string, newValue: FieldsValue, oldValue: FieldsValue): FieldTypes {
    return {
      dsValorNew: attributeName === DemographicInformationFieldsType.STATE_ACRONYM ? newValue?.sgEstado || '' : newValue?.dsEstado || '',
      dsValorOld: attributeName === DemographicInformationFieldsType.STATE_ACRONYM ? oldValue?.sgEstado || '' : oldValue?.dsEstado || '',
      nmAtributo: attributeName,
    };
  }

  addFieldsToNewValues(newValues: Record<string, FieldsValue>, updatedFields: string[], body: BodyProps, oldValues: Record<string, FieldsValue>): Record<string, FieldsValue>  {
    Object.keys(newValues).forEach(key => {
      if (oldValues[key] !== newValues[key] && newValues.nrSequencia != null) {
        updatedFields.push(key.toUpperCase());
        const oldV = oldValues[key];
        const newV = newValues[key];

        if (key === DemographicInformationFieldsType.STATE) {
          const stateAcronymField: FieldTypes = this.createStateField(DemographicInformationFieldsType.STATE_ACRONYM, newV, oldV);
          const stateNameField: FieldTypes = this.createStateField(DemographicInformationFieldsType.STATE_NAME, newV, oldV);
          body.campos.push(stateAcronymField);
          body.campos.push(stateNameField);
        } else {
          body.campos.push({
            dsValorNew: newV ? newV.nrSeqPais || newV.sgEstado || newV.dsMunicipio || newV : '',
            dsValorOld: oldV ? oldV.nrSeqPais || oldV.sgEstado || oldV.dsMunicipio || oldV : '',
            nmAtributo: key,
          });
        }
      }
    });

    return newValues;
  }

  addBoliviaFieldsToNewValues(newValues: Record<string, FieldsValue>, updatedFields: string[], body: BodyProps, oldValues: Record<string, FieldsValue>): Record<string, FieldsValue> {
    const fieldsBoliviaDemographic = ['distrito', 'stateProvince', 'municipio', 'dsStreetBolivia', 'dsComplemento', 'dsNeighborhoodBolivia', 'dsValorLocalidadeBolivia'] as string[];

    const { boliviaDepartments, boliviaStateProvince, boliviaCity, ...restOfValues } = newValues

    newValues = {
      boliviaCity: boliviaCity,
      boliviaDepartments: boliviaDepartments,
      boliviaStateProvince: boliviaStateProvince,
      distrito: boliviaDepartments?.description as unknown,
      municipio: boliviaCity?.dsMunicipio as unknown,
      stateProvince: boliviaStateProvince?.dsStateProvince as unknown,
      ...restOfValues
    } as Record<string, FieldsValue>;

    Object.keys(newValues).forEach((key) => {
      if (newValues.nrSequencia != null && fieldsBoliviaDemographic.includes(key)) {
        updatedFields.push(key.toUpperCase());
        const oldV = oldValues[key];
        const newV = newValues[key];
        body.campos?.push({
          dsValorNew: newV ? newV : '',
          dsValorOld: oldV ? oldV : '',
          nmAtributo: key,
        });
      }
    });

    return newValues;
  }

  getFieldsRequired(updatedFields: string[], body: BodyProps): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_FIELDS_REQUIRE_PROOF).then((resp: HttpResponse<unknown>) => {
      const { body: bodyResponse } = resp as HttpResponse<FieldsRequireProof[]>;
      const fieldsRequireProof = bodyResponse && bodyResponse
        .filter(doc => updatedFields.some((updatedField) => updatedField === doc.ieAttributo.replace(/_/g, '')))
        .map(doc => ({
          attribute: doc.ieAttributo,
          key: doc.nrSequenciaDoc.toString(),
          required: true,
          value: doc.dsDocumento,
        }));
      if (fieldsRequireProof && fieldsRequireProof.length > 0) {
        ProfileModalUploadService.open({
          bodyToUpdate: body,
          mandatoryAttachmentsFields: fieldsRequireProof,
          onConfirmUpload: e => this.sendUpdateRequest(e.record),
        });
      } else {
        this.sendUpdateRequest(this.state.bodyToUpdate);
      }
    });
  }

  sendUpdateRequest(body: Record<string, Value>): void {
    const params = !this.fieldAddressCatalogAPI.isVisible() ? {
      patientId: UserContext.patientId,
      tipoFormulario: TipoFormularioEnum.INFORMACOES_DEMOGRAFICAS
    }:{
      nrSeqCatalog: this.context.get('nrSeqCatalog'),
      patientId: UserContext.patientId,
      tipoFormulario: TipoFormularioEnum.INFORMACOES_DEMOGRAFICAS
    }
    PatientHttpService.post<ToastResponse>(APPLICATION_ENDPOINTS.POST_PROFILE_INFORMATION, params, { body }).then((response) => {
      const insertVal = body?.insertVal as [] || [];
      if(insertVal.length > 0) {
        this.fetchFormDemographicAPI.forceFetch();
      }
      this.notifyComplPessoaFisicaChanged();
      this.handleResponse(response);
    }).catch(({ response }: HttpServiceError<ToastResponse>) => {
      this.handleResponse(response);
    });
  }

  handleResponse(resp?: HttpResponse<ToastResponse>): void {
    if (resp?.body) {
      showToastNotificationMessage(resp.body);
    }
  }

  @OnMount({ target: 'form-demographic' })
  onMountFormDemographic(event: MountEvent): void {
    const formApi = event.target as FormAPI;
    super.setContextVariablesForTaskModule();
    super.listenWhetherProfileEditingIsEnabled(formApi);

    if(this.language === LocaleTypes.BOLIVIA) {
      this.fieldAddressCatalogAPI.setVisible(true);
      this.fieldPostCodeAPI.setVisible(false);
      this.fieldStateAPI.setVisible(false);
      this.fieldCityDisplayAPI.setVisible(false);
      this.fieldAddressAPI.setVisible(false);
      this.fieldNrTelefoneAPI.setVisible(false);
      this.fieldNumberAPI.setVisible(false);
      this.fieldNeighborhoodAPI.setVisible(false);
    }
  }

  @OnBeforeFetch({target: InformationAliases.FETCH_STATE_BOLIVIA })
  handleBeforeFetchFieldStateBolivia(event: FetchEvent): void {
    const fetchStateProvinceBolivia = event.target as FetchAPI;
    if(fetchStateProvinceBolivia) {
      fetchStateProvinceBolivia.setParameters({ nrSeqCatalog: this.context.get('nrSeqCatalog') });
    }
  }

  @OnChange({target: 'field-state-bolivia' })
  onChangeFieldStateBolivia(event: ChangeEvent<StateBoliviaEventProps>): void {
    this.fieldStateProvinceBoliviaAPI.setValue('');
    this.fieldCityBoliviaAPI.setValue('');
    if(event.value) {
      this.fetchStateProvinceBoliviaAPI.setParameters({ address: event.value.sequence, nrSeqCatalog: this.context.get('nrSeqCatalog') });
      this.fetchStateProvinceBoliviaAPI.forceFetch();
    }
  }

  @OnChange({target: 'field-state-province-bolivia'})
  onChangeFieldCityBolivia(event: ChangeEvent<StateProvinceBoliviaEventProps>): void {
    this.fieldCityBoliviaAPI.setValue('');
    if(event.value) {
      this.fetchCityBoliviaAPI.setParameters({ nrSeqCatalog: this.context.get('nrSeqCatalog'), stateProvince: event.value.cdStateProvince })
      this.fetchCityBoliviaAPI.forceFetch();
    }
  }

  private notifyComplPessoaFisicaChanged(): void {
    BrokerService.publishToTopic(BS_REFRESH_FORM_COMPL_PESSOA_FISICA, null);
  }

  @OnBeforeFetch({ target: InformationAliases.FETCH_DEMOGRAPGIC})
  handleBeforeFetchDemographicInformation(event: FetchEvent): void {
    const fetchDemographicInformation = event.target as FetchAPI;
    fetchDemographicInformation.setParameters({ personCode: UserContext.patientId });
  }

  @OnAfterFetch({ target: InformationAliases.FETCH_DEMOGRAPGIC })
  onAfterFetchDemographic(event: FetchEvent): void{
    const data = event.data as Record<string, Value>
    if(data) {
      const addressCatalog = data?.addressCatalog as AddressCatalogProps

      if(addressCatalog) {
        this.fieldAddressCatalogAPI.onChange.fire(new ChangeEvent({ value: {nrSequencia: addressCatalog.nrSequencia} }))
      }
    }
  }

  @OnChange({ target: InformationAliases.FIELD_ADDRESS_CATALOG })
  onChangeFieldAddressCatalog(event: ChangeEvent<AddressCatalogProps>): void {
    if(this.fieldAddressCatalogAPI.isVisible()) {
      this.setVisibleFields();
      this.clearAllFields();

      const nrSeqCatalog = event.value.nrSequencia;
      this.context.set({nrSeqCatalog: event.value.nrSequencia})

      HttpService.get(APPLICATION_ENDPOINTS.GET_ADDRESS_FIELDS, {nrSeqCatalog}).then((resp: HttpResponse<unknown>) => {
        const addressFields = resp.body as AddressFieldsProps[]
        addressFields.forEach(field => {
          this.formDemo.setLoading(true);
          this.fieldsToShow(field);
        })
        this.formDemo.setLoading(false);
        this.fetchStateBoliviaApi.setParameters({ nrSeqCatalog })
        this.fetchStateBoliviaApi.forceFetch();
      })
    }
  }

  fieldsToShow(field: AddressFieldsProps): void {
    this.fieldsToComplete(field);
    this.fieldsToSelect(field);
  }

  fieldsToComplete(field: AddressFieldsProps): void {
    switch (field.ieInformacao) {
      case ConfigurationEnderecoField.RUA_VIALIDADE:
        this.fieldStreetBolivia.setVisible(true);
        if(field.ieObrigatorio === 'S') {
          this.fieldStreetBolivia.addValidator({ name: 'required' });
        }
        break;
      case ConfigurationEnderecoField.COMPLEMENTO:
        this.fieldComplementBoliviaAPI.setVisible(true);
        if(field.ieObrigatorio === 'S') {
          this.fieldComplementBoliviaAPI.addValidator({ name: 'required' });
        }
        break;
      case ConfigurationEnderecoField.BAIRRO_VILA:
        this.fieldNeighborhoodBolivia.setVisible(true);
        if(field.ieObrigatorio === 'S') {
          this.fieldNeighborhoodBolivia.addValidator({ name: 'required' });
        }
        break;
      case ConfigurationEnderecoField.LOCALIDADE_AREA:
        this.fieldLocalidadeBoliviaAPI.setVisible(true);
        if(field.ieObrigatorio === 'S') {
          this.fieldLocalidadeBoliviaAPI.addValidator({ name: 'required' });
        }
        break;
      default:
        break;
    }
  }

  fieldsToSelect(field: AddressFieldsProps): void {
    switch (field.ieInformacao) {
      case ConfigurationEnderecoField.DISTRITO:
        this.fieldStateBoliviaAPI.setVisible(true);
        if(field.ieObrigatorio === 'S') {
          this.fieldStateBoliviaAPI.addValidator({ name: 'required' });
        }
        break;
      case ConfigurationEnderecoField.ESTADO_PROVINCI:
        this.fieldStateProvinceBoliviaAPI.setVisible(true);
        if(field.ieObrigatorio === 'S') {
          this.fieldStateProvinceBoliviaAPI.addValidator({ name: 'required' });
        }
        break;
      case ConfigurationEnderecoField.MUNICIPIO:
        this.fieldCityBoliviaAPI.setVisible(true);
        if(field.ieObrigatorio === 'S') {
          this.fieldCityBoliviaAPI.addValidator({ name: 'required' });
        }
        break;
      default:
        break;
    }
  }

  setVisibleFields(): void {
    this.fieldLocalidadeBoliviaAPI.setVisible(false);
    this.fieldNeighborhoodBolivia.setVisible(false);
    this.fieldComplementBoliviaAPI.setVisible(false);
    this.fieldStreetBolivia.setVisible(false);
    this.fieldCityBoliviaAPI.setVisible(false);
    this.fieldStateProvinceBoliviaAPI.setVisible(false);
    this.fieldStateBoliviaAPI.setVisible(false);
    this.fieldPostCodeAPI.setVisible(false);
    this.fieldStateAPI.setVisible(false);
    this.fieldCityDisplayAPI.setVisible(false);
    this.fieldAddressAPI.setVisible(false);
    this.fieldNrTelefoneAPI.setVisible(false);
    this.fieldNumberAPI.setVisible(false);
    this.fieldNeighborhoodAPI.setVisible(false);
  }

  clearAllFields(): void {
    this.fieldLocalidadeBoliviaAPI.setValue('');
    this.fieldNeighborhoodBolivia.setValue('');
    this.fieldComplementBoliviaAPI.setValue('');
    this.fieldStreetBolivia.setValue('');
    this.fieldCityBoliviaAPI.setValue(undefined);
    this.fieldStateProvinceBoliviaAPI.setValue(undefined);
    this.fieldStateBoliviaAPI.setValue(undefined);
    this.fieldPostCodeAPI.setValue('');
    this.fieldStateAPI.setValue(undefined);
    this.fieldCityDisplayAPI.setValue('');
    this.fieldAddressAPI.setValue('');
    this.fieldNrTelefoneAPI.setValue('');
    this.fieldNumberAPI.setValue('');
    this.fieldNeighborhoodAPI.setValue('');
  }

}

export default DemographicInformationController;
